import {flatten, isNil, isNotNil, map, mergeRight, pipe} from 'ramda';

import {Nullish} from 'shared/web';

import type {
  AuditCategory,
  Category,
  CategoryUniqueKey,
  EquipmentDocument,
  GetVehicleAuditApiResponse,
  LoadAuditDataCategories,
  VehicleAudit,
} from '../digitalCertificateApi';

/**
 * Flattens the audit structure and audit data of the certificate response into the shape the
 * sections consume. Every top-level property of the response has to be carried through here
 * explicitly — the result is built from scratch, not spread from the response.
 */
export function transformVehicleAuditResponse(
  vehicleAudit: GetVehicleAuditApiResponse | Nullish
): VehicleAudit | null {
  if (isNil(vehicleAudit)) {
    return null;
  }
  const auditStructureByAuditId = vehicleAudit.auditStructure?.find(
    (structure) => structure.id === vehicleAudit.audit.controlStructureId
  );

  if (isNil(auditStructureByAuditId)) {
    return null;
  }

  const flattenAuditStructureCategories: Category[] = [];

  const flattenCategories = (
    {childCategories, ...category}: Category,
    parentUniqueKey: CategoryUniqueKey | Nullish
  ) => {
    if (isNotNil(parentUniqueKey)) {
      flattenAuditStructureCategories.push(mergeRight(category, {parentUniqueKey}));
    } else {
      flattenAuditStructureCategories.push(category);
    }
    childCategories?.forEach((c) => flattenCategories(c, parentUniqueKey));
  };

  auditStructureByAuditId.categories.forEach((c) => flattenCategories(c, c.uniqueKey));

  const flattenAuditCategories = pipe<
    [LoadAuditDataCategories[]],
    AuditCategory[][][],
    AuditCategory[]
  >(
    map((category) => [
      category.fields.map((field) => ({...field, type: 'field'})),
      category.assets.map((asset) => ({...asset, type: 'assets'})),
      category.videoAssets.map((videoAsset) => ({...videoAsset, type: 'videoAssets'})),
    ]),
    flatten
  )(vehicleAudit.audit.categories);

  return {
    assignments: vehicleAudit.assignments,
    vehicle: vehicleAudit.vehicle,
    audit: mergeRight(vehicleAudit.audit, {categories: flattenAuditCategories}),
    auditStructure: mergeRight(auditStructureByAuditId, {
      categories: flattenAuditStructureCategories,
    }),
    showEmptyFields: vehicleAudit.showEmptyFields,
    equipmentDocument: getLinkableEquipmentDocument(vehicleAudit.equipmentDocument),
  };
}

/**
 * The certificate is a public page and its response carries no schema, so the download URL is
 * checked here before it becomes a link target: anything but an absolute `https:` URL drops the
 * document instead of rendering a `javascript:` or `data:` link.
 */
const getLinkableEquipmentDocument = (equipmentDocument: EquipmentDocument | Nullish) =>
  isNotNil(equipmentDocument) && isHttpsUrl(equipmentDocument.downloadUrl)
    ? equipmentDocument
    : null;

const isHttpsUrl = (url: string) => {
  try {
    return new URL(url).protocol === 'https:';
  } catch {
    return false;
  }
};
